import React, { useEffect, useState, useRef, useContext } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { APP_TITLE } from "../../../config/constant";
import { useOutletContext } from "react-router";
import { useSelector } from "react-redux";
import FormCard from "../../common/forms/FormCard";
import FormField from "../../common/forms/FormField";
import SimpleButtonContentHeader from "../../common/mainApp/mainWindowHeaders/SimpleButtonContentHeader";
import FormFieldTagsDropdown from "../../common/forms/FormFieldTagsDropdown";
import * as Library from "../../../utils/Library";
import { useParams } from "react-router-dom";
import AppContext from "../../../AppContext";
import SmallFormFieldUpload from "../../common/uploadImages/SmallFormFieldUpload";
import FullScreenOverlay from "../../common/overlay/FullScreenOverlay";
import DialogBoxComponent from "../../common/dialogs/DialogBoxComponent";
import { formatPhoneNumber } from "../../../utils/Formatter";

// #region SCOPED CSS
const StyledPage = styled.div`
    .input-remove-default {
        border: none;
        outline: none;
        background: none;
    }

    .selects {
        background: var(--backgrounds-and-lines-n-600, #10141b);
    }

    .selects.mandatory {
        border: 1px solid transparent;
        border-left: 5px solid var(--text-icons-orange-500);
        border-radius: 4px;
        outline: none;
    }

    .selects.mandatory > div {
        border: none;
        cursor: pointer;
    }

    .selects.mandatory.error {
        border: 1px solid var(--text-icons-red-500);
        border-left: 5px solid var(--text-icons-red-500);
    }

    .main-window-header {
        background: var(--backgrounds-and-lines-n-600, #10141b);
        display: flex;
        flex-direction: row;
        row-gap: 0px;
        align-items: center;
        justify-content: space-between;
        flex-shrink: 0;
        height: 68px;
        position: relative;
    }
    .frame-40 {
        display: flex;
        flex-direction: row;
        gap: 8px;
        align-items: center;
        justify-content: flex-end;
        flex: 1;
        position: relative;
    }
    .btn-large-solid {
        background: var(--buttons-all-platforms-gray-300, #1e2631);
        border-radius: 4px;
        padding: 10px;
        display: flex;
        flex-direction: row;
        gap: 10px;
        row-gap: 0px;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;
        width: 120px;
        height: 35px;
        position: relative;
    }
    .button {
        color: var(--buttons-all-platforms-gray-200, #4f5b6d);
        text-align: left;
        font-family: var(--button-text-small-font-family, "Roboto-Regular", sans-serif);
        font-size: var(--button-text-small-font-size, 10px);
        letter-spacing: var(--button-text-small-letter-spacing, 0.0125em);
        font-weight: var(--button-text-small-font-weight, 400);
        text-transform: uppercase;
        position: relative;
    }
`;
// #endregion SCOPED CSS

export default function UserRequests() {
    // #region Page Title
    document.title = `${APP_TITLE} - Users Edit`;
    // #endregion Page Title

    // #region VARS
    const { id } = useParams();
    const { userData, setHasUnsavedChanges } = useContext(AppContext);
    const isHisOwnUser = userData?.BOStagesAccount?._id === id;
    const navigate = useNavigate();
    const formUserInviteID = useRef();
    const formUserInviteRefs = [];
    const { MainLayoutProps, layoutType } = useOutletContext();
    const navigations = useSelector(state => state.navigationAccesses);
    const [isLoading, setIsLoading] = useState(false);
    const [editBtnDisabled, setEditBtnDisabled] = useState(false);
    const [imageData, setImageData] = useState(null);
    const [showDialogBox, setShowDialogBox] = useState(false);

    const [profileHasRole, setProfileHasRole] = useState(false);

    const props = {
        layoutType: layoutType,
        setControlBarLeftBtns: MainLayoutProps?.setControlBarLeftBtns,
        setControlBarCenterBtns: MainLayoutProps?.setControlBarCenterBtns,
        setControlBarRightBtns: MainLayoutProps?.setControlBarRightBtns,
        setFilterHeaderProperties: MainLayoutProps?.setFilterHeaderProperties,
        setRefreshUserData: MainLayoutProps?.setRefreshUserData
    };
    const userRole = userData?.role;
    const [inviteFormData, setInviteFormData] = useState({
        accessLevel: "",
        toAccount: "",
        firstName: "",
        lastName: "",
        email: "",
        role: "",
        phoneNumber: ""
    });

    // Dynamically set accessLevelDataOptions based on userRole
    let accessLevelDataOptions = [
        { id: "account", name: "Organization level", value: "Organization level" },
        { id: "company", name: "Company level", value: "Company level" },
        { id: "brand", name: "Retail Brand level", value: "Retail Brand level" }
    ];

    // Use a locked map for accessLevelDataOptions just like for roles
    const lockedAccessLevelMap = {
        "Company Account Admin": ["account"],
        "Company Account Manager": ["account"],
        "Company Account User": ["account"],
        "Retail Brand Account Admin": ["account", "company"],
        "Retail Brand Account Manager": ["account", "company"],
        "Retail Brand Account User": ["account", "company", "brand"]
    };
    const lockedAccessLevels = lockedAccessLevelMap[userRole] || [];
    if (lockedAccessLevels.length) {
        accessLevelDataOptions = accessLevelDataOptions.map(option =>
            lockedAccessLevels.includes(option.id) ? { ...option, locked: true } : option
        );
    }

    const roleOptions = [
        [
            { name: "Account owner", value: "Account owner" },
            { name: "Account Admin", value: "Account Admin" },
            { name: "Account Manager", value: "Account Manager" },
            { name: "Account User", value: "Account User" }
        ],
        [
            { name: "Company Account Admin", value: "Company Account Admin" },
            { name: "Company Account Manager", value: "Company Account Manager" },
            { name: "Company Account User", value: "Company Account User" }
        ],
        [
            { name: "Retail Brand Account Admin", value: "Retail Brand Account Admin" },
            { name: "Retail Brand Account Manager", value: "Retail Brand Account Manager" },
            { name: "Retail Brand Account User", value: "Retail Brand Account User" }
        ]
    ];

    const roleOptionIndex =
        inviteFormData.accessLevel === "Organization level"
            ? 0
            : inviteFormData.accessLevel === "Company level"
            ? 1
            : 2;
    // #endregion VARS

    // #region METHODS
    const handleEditBtnClick = () => {
        setShowDialogBox(true);
    };

    const handleSaveClicked = () => {
        const formData = formUserInviteID?.current?.getAllFieldValues();
        const companyIDs = formData?.toCompanies;
        const labelBrandIDs = formData?.toLabelBrand;
        // Map tagName in companyIDs to value in allCompanies where tagName === name
        const selectedCompanyValues = companyIDs
            ? companyIDs
                  .map(company => allCompanies.find(c => c.name.trim() === company.tagName.trim())?.value)
                  .filter(Boolean)
            : [];
        const selectedLabelBrandValues = labelBrandIDs
            ? labelBrandIDs
                  .map(labelBrandId => allBrands.find(b => b.name.trim() === labelBrandId.tagName.trim())?.value)
                  .filter(Boolean)
            : [];

        const phoneNum = formData.phoneNumber.split(" ")[1];
        const phoneNumCountryCode = formData.phoneNumber?.split(" ")[0];
        const payload = {
            userID: id,
            email: formData.email,
            firstName: formData.firstName,
            lastName: formData.lastName,
            phoneNumber: phoneNum,
            phoneNumberCountryCode: phoneNumCountryCode,
            role: formData.role?.name || "", // fallback to empty string if undefined
            accessLevel: formData.accessLevel?.name || "", // fallback to empty string if undefined
            organizationID: localStorage.getItem("currentOrganizationID"),
            restrictions: {
                companyIDs: formData.accessLevel?.value === "Company level" ? selectedCompanyValues : undefined,
                retailBrandIDs:
                    formData.accessLevel?.value === "Retail Brand level" ? selectedLabelBrandValues : undefined
            },
            profilePhotoData: imageData?.base64 ? { ...imageData } : undefined,
            profilePhotoURL: imageData?.url || ""
        };

        setHasUnsavedChanges(false);

        Library.makeCommonPostRequest("Users", "editUser", payload, false, setIsLoading).then(res => {
            if (res?.data?.status === 200) {
                Library.showToastMessage({
                    type: "success",
                    title: "User Updated",
                    message: `${formData.firstName}'s profile has been updated successfully.`
                });
                navigate("/users");
            } else {
                // Handle error
            }
        });
    };

    const checkFormIsValid = () => {
        const allCompanyIdFieldsAreValid = formUserInviteID.current.allFieldsAreValid(false);
        const allFieldsAreValid = allCompanyIdFieldsAreValid;

        setEditBtnDisabled(allFieldsAreValid);
    };

    const handleAccessLevelChange = value => {
        if (value?.locked) {
            value.value = "";
        }
        const updatedInviteFormData = { ...inviteFormData, accessLevel: value?.value };

        if (value?.value === "Organization level") {
            updatedInviteFormData.toAccount = localStorage.getItem("currentOrganizationName") || "";
            updatedInviteFormData.toCompanies = undefined; // Clear other fields
            updatedInviteFormData.toLabelBrand = undefined;
        } else if (value?.value === "Company level") {
            updatedInviteFormData.toAccount = "";
            updatedInviteFormData.toCompanies = allCompanies.length === 1 ? [allCompanies[0]?.name] : [];
            updatedInviteFormData.toLabelBrand = undefined;
        } else if (value?.value === "Retail Brand level") {
            updatedInviteFormData.toAccount = "";
            updatedInviteFormData.toCompanies = undefined;
            updatedInviteFormData.toLabelBrand = allBrands.length === 1 ? [allBrands[0]?.name] : [];
        }

        setInviteFormData(updatedInviteFormData);
    };

    // Filter the role options based on the user's role
    const getFilteredRoleOptions = () => {
        let options = roleOptions?.[roleOptionIndex] || [];
        const lockedRolesMap = {
            "Account Admin": ["Account owner", "Account Admin"],
            "Account Manager": ["Account owner", "Account Admin", "Account Manager"],
            "Account User": ["Account owner", "Account Admin", "Account Manager", "Account User"],
            "Company Account Admin": ["Company Account Admin"],
            "Company Account Manager": ["Company Account Admin", "Company Account Manager"],
            "Company Account User": ["Company Account Admin", "Company Account Manager", "Company Account User"],
            "Retail Brand Account Admin": ["Retail Brand Account Admin"],
            "Retail Brand Account Manager": ["Retail Brand Account Admin", "Retail Brand Account Manager"],
            "Retail Brand Account User": [
                "Retail Brand Account Admin",
                "Retail Brand Account Manager",
                "Retail Brand Account User"
            ]
        };
        const locked = lockedRolesMap[userRole] || [];
        if (locked.length) {
            options = options.map(option =>
                locked.includes(option.value) ? { ...option, locked: profileHasRole ? true : false } : option
            );
        }
        return options;
    };

    const roleOptionsFiltered = getFilteredRoleOptions();

    const getAllToBrands = () => {
        const toBrands = [];
        const retailBrands = JSON.parse(localStorage.getItem("retailBrands"));

        retailBrands?.forEach(i => {
            toBrands.push({ value: i?._id, name: i?.name });
        });
        return toBrands;
    };
    const [allBrands] = useState(getAllToBrands());

    const handleRoleChange = value => {
        if (value?.locked) {
            value.value = "";
        }
        setInviteFormData({ ...inviteFormData, role: value?.value }); // Update role in state
        checkFormIsValid(); // Revalidate the form
    };

    const [allCompanies] = useState([
        {
            name: localStorage.getItem("currentCompanyName"),
            value: localStorage.getItem("currentCompanyID")
        }
    ]);
    // #endregion METHODS

    // #region EFFECTS
    useEffect(() => {
        //Get user data by id
        Library.makeCommonPostRequest("Users", "getUser", { userID: id }, false, null).then(res => {
            if (res?.status === 200) {
                const userData = res?.data?.data;
                const jsonResponseData = {
                    accessLevel: userData?.accessLevel,
                    firstName: userData?.firstName,
                    lastName: userData?.lastName,
                    email: userData?.email,
                    role: userData?.role,
                    phoneNumber: userData?.phoneNumber,
                    phoneNumberCountryCode: userData?.phoneNumberCountryCode?.replace("+", "")
                };
                setProfileHasRole(userData?.role ? true : false);
                if (userData?.accessLevel === "Organization level") {
                    jsonResponseData.toAccount = localStorage.getItem("currentOrganizationName") || "";
                    jsonResponseData.toCompanies = undefined; // Clear other fields
                    jsonResponseData.toLabelBrand = undefined;
                } else if (userData?.accessLevel === "Company level") {
                    jsonResponseData.toAccount = "";
                    jsonResponseData.toCompanies = allCompanies
                        .filter(company =>
                            Array.isArray(userData?.restrictions?.companyIDs)
                                ? userData.restrictions.companyIDs.includes(company.value)
                                : company.value === userData?.restrictions?.companyIDs
                        )
                        .map(company => (company.name ? company.name : company.value));
                    jsonResponseData.toLabelBrand = undefined;
                } else if (userData?.accessLevel === "Retail Brand level") {
                    jsonResponseData.toAccount = "";
                    jsonResponseData.toCompanies = undefined;
                    jsonResponseData.toLabelBrand = allBrands
                        .filter(brand =>
                            Array.isArray(userData?.restrictions?.retailBrandIDs)
                                ? userData.restrictions.retailBrandIDs.includes(brand.value)
                                : brand.value === userData?.restrictions?.retailBrandIDs
                        )
                        .map(brand => (brand.name ? brand.name : brand.value));
                }
                setInviteFormData(jsonResponseData);
                setImageData(userData?.profilePhotoUrl ? { url: userData.profilePhotoUrl } : null);
            } else {
                // Handle error
            }
        });
    }, []);

    useEffect(() => {
        const editIsEnabled = editBtnDisabled && !isLoading;

        setupHeaders(props, editIsEnabled, handleEditBtnClick, navigations);
    }, [props?.layoutType, editBtnDisabled, isLoading]);

    // #endregion EFFECTS

    // #region FORM FIELDS
    const formUserInviteFields = [
        <FormField
            type="dropdown"
            dropdownData={accessLevelDataOptions}
            title="Access level"
            assistiveText=""
            isDropdownSearchable={false}
            tooltip="Select access level. This field is not mandatory."
            tooltipMaxWidth="166px"
            requiredType={isHisOwnUser ? "mandatoryLocked" : "mandatory"}
            varName="accessLevel"
            ref={el => (formUserInviteRefs.accessLevel = el)}
            value={inviteFormData.accessLevel} // Bind the value to the state
            onDropdownChange={handleAccessLevelChange}
            placeholder="Select access level"
        />,
        inviteFormData.accessLevel === "Organization level" || inviteFormData.accessLevel === "" ? (
            <FormField
                type="text"
                title="To Account"
                className="input-remove-default"
                tooltip="This field is locked with the account name."
                tooltipMaxWidth="180px"
                requiredType="mandatoryLocked"
                assistiveText=""
                varName="toAccount"
                ref={el => (formUserInviteRefs.toAccount = el)}
                value={inviteFormData.toAccount}
            />
        ) : (
            <FormFieldTagsDropdown
                type="text"
                autoCompleteStaticData={inviteFormData.accessLevel === "Company level" ? allCompanies : allBrands}
                title={inviteFormData.accessLevel === "Company level" ? "To Companies" : "To Label Brands"}
                tooltip={
                    inviteFormData.accessLevel === "Company level"
                        ? "Enter the company you want to send the invite to. Ensure the company name is accurate."
                        : "Enter the brand you want to send the invite from. This is your brand. If you only have one brand enrolled, the field will be locked."
                }
                tooltipMaxWidth="125px"
                //requiredType={isHisOwnUser ? "mandatoryLocked" : "mandatory"}
                allowNewTags={false}
                varName={inviteFormData.accessLevel === "Company level" ? "toCompanies" : "toLabelBrand"}
                ref={el =>
                    inviteFormData.accessLevel === "Company level"
                        ? (formUserInviteRefs.toCompanies = el)
                        : (formUserInviteRefs.toLabelBrand = el)
                }
                value={
                    inviteFormData.accessLevel === "Company level"
                        ? inviteFormData.toCompanies
                        : inviteFormData.toLabelBrand
                }
                requiredType={
                    isHisOwnUser
                        ? "mandatoryLocked"
                        : inviteFormData.accessLevel === "Company level" && allCompanies.length === 1
                        ? "mandatoryLocked"
                        : inviteFormData.accessLevel === "Label Brand level" && allBrands.length === 1
                        ? "mandatoryLocked"
                        : "mandatory"
                }
                disableTagRemove={
                    isHisOwnUser
                        ? true
                        : inviteFormData.accessLevel === "Company level" && allCompanies.length === 1
                        ? true
                        : inviteFormData.accessLevel === "Label Brand level" && allBrands.length === 1
                        ? true
                        : false
                }
                useSelectAll={true}
                useDoneButton={true}
                selectAllText="Select All"
            />
        ),
        <FormField
            type="text"
            title="First name"
            tooltip="Enter the first name of the user to be invited. This field is not mandatory."
            tooltipMaxWidth="224px"
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            assistiveText=""
            varName="firstName"
            ref={el => (formUserInviteRefs.firstName = el)}
            value={inviteFormData.firstName}
        />,
        <FormField
            type="text"
            title="Last name"
            tooltip="Enter the last name of the user to be invited. This field is not mandatory."
            tooltipMaxWidth="224px"
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            assistiveText=""
            varName="lastName"
            ref={el => (formUserInviteRefs.lastName = el)}
            value={inviteFormData.lastName}
        />,
        <FormField
            type="dropdown"
            dropdownData={roleOptionsFiltered}
            title="Role"
            assistiveText=""
            isDropdownSearchable={false}
            tooltip="Select role. This field is not mandatory."
            tooltipMaxWidth="166px"
            requiredType={isHisOwnUser ? (profileHasRole ? "mandatoryLocked" : "mandatory") : "mandatory"}
            varName="role"
            ref={el => (formUserInviteRefs.role = el)}
            value={inviteFormData?.role}
            onDropdownChange={handleRoleChange}
        />,
        <FormField
            type="phoneNumber"
            title="Phone number"
            tooltip="Enter the phone number of the user to be invited. This field is not mandatory."
            requiredType={inviteFormData.accessLevel === "" ? "mandatoryLocked" : "mandatory"}
            varName="phoneNumber"
            ref={el => (formUserInviteRefs.phoneNumber = el)}
            //value={inviteFormData.phoneNumber}
            value={formatPhoneNumber(inviteFormData?.phoneNumberCountryCode, inviteFormData?.phoneNumber)}
            phoneNumberCountryCode={inviteFormData?.phoneNumberCountryCode}
            onCountryCodeChange={e => {
                setInviteFormData({
                    ...inviteFormData,
                    phoneNumberCountryCode: e.value
                });
            }}
            onTextChange={text => {
                setInviteFormData({
                    ...inviteFormData,
                    phoneNumber: text
                });
            }}
        />,
        <FormField
            type="email"
            title="Email"
            tooltip="Enter the email address of the user to be invited. This field is not mandatory."
            tooltipMaxWidth="224px"
            requiredType={isHisOwnUser ? "locked" : "locked"}
            assistiveText=""
            varName="email"
            ref={el => (formUserInviteRefs.email = el)}
            value={inviteFormData.email}
        />
    ];
    // #endregion FORM FIELDS

    return (
        <StyledPage>
            <FullScreenOverlay show={showDialogBox}>
                <DialogBoxComponent
                    headingColor="var(--text-icons-green-500)"
                    title="Ready to save?"
                    message="You can still change it later. No worries:)"
                    onClickYes={() => {
                        handleSaveClicked();
                    }}
                    loading={isLoading}
                    loadingHeading={"SAVING..."}
                    onClickNo={() => {
                        setShowDialogBox(false);
                    }}
                    cancelText="No"
                    confirmText="Yes"
                />
            </FullScreenOverlay>
            <FormCard
                title="EDIT USER"
                ref={formUserInviteID}
                numOfFieldsPerLine={4}
                showDefaultRightLegends={true}
                formFields={formUserInviteFields}
                formFieldRefs={formUserInviteRefs}
                onFormChanged={checkFormIsValid}
                showUnsavedChangesPopup={true}
                rightContent={SmallFormFieldUpload({ imageData, setImageData })}
            />
        </StyledPage>
    );
}

function setupHeaders(props, onOpenDialod, onBtnClicked) {
    props.setControlBarLeftBtns([]);
    props.setControlBarCenterBtns([
        { to: "/users", active: true, disabled: false, title: "Users" },
        { to: "/users/invitations", active: false, disabled: false, title: "INVITES" }
    ]);
    props.setControlBarRightBtns([
        { to: "/users", active: false, disabled: false, title: "Back", icon: "chevron-left" },
        { to: "#", active: true, disabled: false, title: "Edit", icon: "edit" }
    ]);

    props.setFilterHeaderProperties({
        customContentHeader: (
            <SimpleButtonContentHeader btnTitle="UPDATE" btnEnabled={onOpenDialod} onClick={onBtnClicked} />
        )
    });
}
